import React, { useCallback, useMemo, useRef, useState } from 'react';
import { node } from 'prop-types';
import WebSocket from 'react-websocket';
import { errorLogger } from '@ajaibid/utils';
import { useUserData } from '@services/users/me';

const { WEBSOCKET_PRICE } = process.env;

export const PriceSocketContext = React.createContext();

const PriceSocketProvider = ({ children }) => {
  const wsRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const [prices, setPrices] = useState({});
  const [listeners, setListeners] = useState({});
  const { userData } = useUserData();

  /**
   * @type {object[]}
   */
  const activeCodes = useMemo(() => {
    return Object.keys(listeners).filter((key) => listeners[key] > 0);
  }, [listeners]);

  const subscribe = useCallback(
    (code) => {
      if (!isActive) {
        return;
      }

      wsRef.current.sendMessage(
        JSON.stringify({
          id: userData?.id,
          method: 'SUBSCRIBE',
          stream: 'PRICE',
          streamObject: encodeURI(code),
        })
      );
    },
    [isActive]
  );

  const unsubscribe = useCallback(
    (code) => {
      if (!isActive) {
        return;
      }

      wsRef.current.sendMessage(
        JSON.stringify({
          id: userData?.id,
          method: 'UNSUBSCRIBE',
          stream: 'PRICE',
          streamObject: encodeURI(code),
        })
      );
    },
    [isActive]
  );

  const subscribeActiveCodes = useCallback(() => {
    activeCodes.forEach((obj) => {
      subscribe(obj);
    });
  }, [activeCodes]);

  const handleOpen = () => {
    setIsActive(true);
    subscribeActiveCodes();
  };

  const handleMessage = (textMessage) => {
    try {
      const message = JSON.parse(textMessage);
      if (!message.ticker) {
        return;
      }
      setPrices((oldPrices) => {
        return { ...oldPrices, [message.ticker]: message };
      });
    } catch (err) {
      errorLogger('ERR_WEBSOCKET_PRICE_HANDLE_MESSAGE', { err });
    }
  };

  const listen = useCallback(
    (code) => {
      setListeners((oldListeners) => {
        if (typeof oldListeners[code] !== 'number') {
          subscribe(code);
        }

        const lastCount = oldListeners[code] || 0;
        return { ...oldListeners, [code]: lastCount + 1 };
      });
    },
    [setListeners, subscribe]
  );

  const unlisten = useCallback(
    (code) => {
      setListeners((oldListeners) => {
        const lastCount = oldListeners[code];
        if (lastCount === 1) {
          unsubscribe(code);
        }
        const newCount = lastCount === 1 ? null : lastCount - 1;

        return { ...oldListeners, [code]: newCount };
      });
    },
    [setListeners, unsubscribe]
  );

  const contextProps = useMemo(
    () => ({
      prices,
      listen,
      unlisten,
    }),
    [prices, listen, unlisten]
  );

  return (
    <PriceSocketContext.Provider value={contextProps}>
      {children}
      <WebSocket
        url={WEBSOCKET_PRICE}
        ref={wsRef}
        onOpen={handleOpen}
        onClose={() => {
          setIsActive(false);
        }}
        reconnect
        onMessage={handleMessage}
        debug={process.env.ENVIRONMENT === 'development'}
      />
    </PriceSocketContext.Provider>
  );
};

PriceSocketProvider.propTypes = {
  children: node,
};

PriceSocketProvider.defaultProps = {
  children: null,
};

export default PriceSocketProvider;
